import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Plus, Search, Trash2, Edit2, GripVertical, Save, X, Upload,
  RefreshCw, Image as ImageIcon, Package, ShieldCheck, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

// ─────────────────────────  Root  ─────────────────────────
export const CrmAddons = () => {
  const [tab, setTab] = useState('items');
  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="crm-addons-root">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#FF3B6B]/15 flex items-center justify-center">
          <Package className="w-5 h-5 text-[#FF3B6B]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Add-on Menu</h1>
          <p className="text-sm text-[#6B6B7A]">Manage the extras customers can add to their party.</p>
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F1EFE9] p-1 rounded-2xl">
          <TabsTrigger value="items" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="addons-tab-items">Items</TabsTrigger>
          <TabsTrigger value="categories" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="addons-tab-categories">Categories</TabsTrigger>
        </TabsList>
        <TabsContent value="items" className="mt-4"><ItemsTab /></TabsContent>
        <TabsContent value="categories" className="mt-4"><CategoriesTab /></TabsContent>
      </Tabs>
    </div>
  );
};

// ─────────────────────────  Items tab  ─────────────────────────
const ItemsTab = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [editing, setEditing] = useState(null); // null | 'new' | item
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ir, cr] = await Promise.all([
        axios.get(`${API}/api/admin/addons/items`, { params: { limit: 500 }, withCredentials: true }),
        axios.get(`${API}/api/admin/addons/categories`, { withCredentials: true }),
      ]);
      setItems(ir.data.rows || []);
      setCategories(cr.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load items');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const catById = useMemo(() => Object.fromEntries(categories.map((c) => [c.category_id, c])), [categories]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((it) =>
      (!catFilter || it.category_id === catFilter) &&
      (!needle || it.name.toLowerCase().includes(needle) ||
        (catById[it.category_id]?.name || '').toLowerCase().includes(needle)),
    );
  }, [items, q, catFilter, catById]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [q, catFilter]);

  const onToggle = async (item) => {
    try {
      await axios.put(`${API}/api/admin/addons/items/${item.item_id}`,
        { active: !item.active }, { withCredentials: true });
      toast.success(item.active ? 'Item deactivated' : 'Item activated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to toggle'); }
  };

  const onDelete = async (item) => {
    if (!window.confirm(item.protected
      ? `"${item.name}" is protected (linked to Design Studio / Loyalty).\nIt will be DEACTIVATED, not deleted. Continue?`
      : `Delete "${item.name}"? This can't be undone.`))
      return;
    try {
      await axios.delete(`${API}/api/admin/addons/items/${item.item_id}`, { withCredentials: true });
      toast.success('Item removed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const onDropReorder = async (targetItem) => {
    if (!dragId || dragId === targetItem.item_id) return;
    // Only reorder within same category (spec: category-level ordering)
    const dragged = items.find((i) => i.item_id === dragId);
    if (!dragged || dragged.category_id !== targetItem.category_id) {
      setDragId(null);
      toast.error('Reorder only allowed within the same category');
      return;
    }
    const catItems = items.filter((i) => i.category_id === targetItem.category_id).sort((a, b) => (a.order || 0) - (b.order || 0));
    const from = catItems.findIndex((i) => i.item_id === dragId);
    const to = catItems.findIndex((i) => i.item_id === targetItem.item_id);
    const reordered = [...catItems];
    reordered.splice(to, 0, reordered.splice(from, 1)[0]);
    const payload = reordered.map((r, idx) => ({ id: r.item_id, order: idx + 1 }));
    try {
      await axios.post(`${API}/api/admin/addons/items/reorder`, { order: payload }, { withCredentials: true });
      toast.success('Order updated');
      load();
    } catch (e) { toast.error('Reorder failed'); }
    setDragId(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B7A]" />
          <Input placeholder="Search name or category…" value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="addons-search" />
        </div>
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
          className="h-9 rounded-xl border border-[#E8E5DF] px-3 text-sm bg-white" data-testid="addons-cat-filter">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.icon} {c.name}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={load} className="rounded-full text-xs">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <Button size="sm" onClick={() => setEditing('new')}
          className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs ml-auto"
          data-testid="addons-new-item">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Item
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFDF8] text-left text-[11px] uppercase text-[#6B6B7A]">
            <tr>
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2 w-16">Image</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2 text-right">Price</th>
              <th className="px-3 py-2 text-center">Active</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-10 text-[#6B6B7A]"><RefreshCw className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-[#6B6B7A]" data-testid="addons-empty">No items match.</td></tr>
            ) : pageRows.map((it) => (
              <tr key={it.item_id}
                draggable onDragStart={() => setDragId(it.item_id)}
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDropReorder(it)}
                className={`border-t border-[#F0EDE6] hover:bg-[#FFFDF8] ${dragId === it.item_id ? 'opacity-50' : ''}`}
                data-testid={`addons-row-${it.item_id}`}>
                <td className="px-3 py-2 text-[#6B6B7A]"><GripVertical className="w-4 h-4 cursor-grab" /></td>
                <td className="px-3 py-2">
                  {it.image_url ? (
                    <img src={`${API}${it.image_url}`} alt={it.name} className="w-12 h-12 object-cover rounded-lg" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-[#FFEDF3] flex items-center justify-center">
                      <ImageIcon className="w-4 h-4 text-[#FF3B6B]" />
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{it.name}</span>
                    {it.protected && <Badge variant="outline" className="text-[9px]"><ShieldCheck className="w-2.5 h-2.5 mr-0.5" /> protected</Badge>}
                  </div>
                  {it.description && <p className="text-[11px] text-[#6B6B7A] mt-0.5 line-clamp-1">{it.description}</p>}
                </td>
                <td className="px-3 py-2 text-xs">{catById[it.category_id]?.icon} {catById[it.category_id]?.name || '—'}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtMoney(it.price)}</td>
                <td className="px-3 py-2 text-center">
                  <Switch checked={!!it.active} onCheckedChange={() => onToggle(it)}
                    data-testid={`addons-active-${it.item_id}`} />
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(it)} className="h-7 w-7 p-0"
                    data-testid={`addons-edit-${it.item_id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(it)} className="h-7 w-7 p-0 text-[#D4302A]"
                    data-testid={`addons-del-${it.item_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length > pageSize && (
        <div className="flex justify-between items-center text-xs text-[#6B6B7A]">
          <span>{filtered.length} items · Page {page}/{pageCount}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs rounded-full">Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)} className="h-7 text-xs rounded-full">Next</Button>
          </div>
        </div>
      )}

      {editing && (
        <ItemEditor
          item={editing === 'new' ? null : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

// ─────────────────────────  Item Editor Modal  ─────────────────────────
const ItemEditor = ({ item, categories, onClose, onSaved }) => {
  const [form, setForm] = useState({
    category_id: item?.category_id || categories[0]?.category_id || '',
    name: item?.name || '',
    price: item?.price ?? 0,
    description: item?.description || '',
    max_qty: item?.max_qty ?? 50,
    unit_label: item?.unit_label || 'each',
    active: item?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(item?.image_url || '');

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.category_id) { toast.error('Select a category'); return; }
    if (Number(form.price) < 0) { toast.error('Price must be ≥ 0'); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), max_qty: Number(form.max_qty), image_url: imageUrl || null };
      if (item) {
        await axios.put(`${API}/api/admin/addons/items/${item.item_id}`, payload, { withCredentials: true });
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/api/admin/addons/items`, payload, { withCredentials: true });
        toast.success('Item created');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!item) { toast.error('Save the item first, then upload an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be ≤ 5 MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/api/admin/addons/items/${item.item_id}/image`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setImageUrl(r.data.image_url);
      toast.success('Image uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="addons-item-editor">
        <DialogHeader>
          <DialogTitle>{item ? `Edit "${item.name}"` : 'New Add-On Item'}</DialogTitle>
          <DialogDescription className="text-xs">
            {item?.protected && (
              <span className="text-[#FF7A2E]">This item is linked to Design Studio / Loyalty and cannot be deleted (only deactivated).</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">Image</label>
            {imageUrl ? (
              <div className="flex items-center gap-3">
                <img src={`${API}${imageUrl}`} alt="" className="w-20 h-20 object-cover rounded-xl" />
                <div className="flex-1">
                  <p className="text-[11px] text-[#6B6B7A] break-all">{imageUrl}</p>
                  <Button size="sm" variant="ghost" onClick={() => setImageUrl('')} className="h-7 text-xs">
                    <X className="w-3 h-3 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            ) : (
              <label className="border-2 border-dashed border-[#E8E5DF] rounded-xl p-4 flex flex-col items-center gap-1 cursor-pointer hover:border-[#FF3B6B] transition-colors">
                <Upload className="w-5 h-5 text-[#6B6B7A]" />
                <span className="text-xs text-[#6B6B7A]">
                  {uploading ? 'Uploading…' : item ? 'Click to upload (JPG/PNG/WEBP, ≤ 5 MB)' : 'Save item first to enable upload'}
                </span>
                <input type="file" accept="image/*" className="hidden" disabled={!item || uploading}
                  onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="addons-image-input" />
              </label>
            )}
          </div>
          <div>
            <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">Name *</label>
            <Input value={form.name} onChange={(e) => setField('name', e.target.value)}
              className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="addons-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">Category *</label>
              <select value={form.category_id} onChange={(e) => setField('category_id', e.target.value)}
                className="h-9 rounded-xl border border-[#E8E5DF] px-3 text-sm bg-white w-full" data-testid="addons-cat">
                {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.icon} {c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">Price ($) *</label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => setField('price', e.target.value)}
                className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="addons-price" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">Max Qty</label>
              <Input type="number" value={form.max_qty} onChange={(e) => setField('max_qty', e.target.value)}
                className="h-9 rounded-xl border-[#E8E5DF] text-sm" />
            </div>
            <div>
              <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">Unit Label</label>
              <Input value={form.unit_label} onChange={(e) => setField('unit_label', e.target.value)}
                placeholder="each · pack · flat" className="h-9 rounded-xl border-[#E8E5DF] text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">Description</label>
            <Textarea value={form.description} onChange={(e) => setField('description', e.target.value)}
              rows={2} className="rounded-xl border-[#E8E5DF] text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => setField('active', v)} data-testid="addons-active" />
            <span className="text-sm">Active (visible on customer booking page)</span>
          </div>
        </div>

        <DialogFooter className="gap-2 pt-3">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            data-testid="addons-save">
            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────  Categories tab  ─────────────────────────
const CategoriesTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/addons/categories`, { withCredentials: true });
      setRows(r.data.rows || []);
    } catch (e) { toast.error('Failed to load categories'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onToggle = async (c) => {
    try {
      await axios.put(`${API}/api/admin/addons/categories/${c.category_id}`,
        { active: !c.active }, { withCredentials: true });
      load();
    } catch (e) { toast.error('Failed to toggle'); }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Refuses if any items are active in it.`)) return;
    try {
      await axios.delete(`${API}/api/admin/addons/categories/${c.category_id}`, { withCredentials: true });
      toast.success('Category deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const onDropReorder = async (target) => {
    if (!dragId || dragId === target.category_id) return;
    const src = rows.findIndex((r) => r.category_id === dragId);
    const dst = rows.findIndex((r) => r.category_id === target.category_id);
    const reordered = [...rows];
    reordered.splice(dst, 0, reordered.splice(src, 1)[0]);
    const payload = reordered.map((r, idx) => ({ id: r.category_id, order: idx + 1 }));
    try {
      await axios.post(`${API}/api/admin/addons/categories/reorder`, { order: payload }, { withCredentials: true });
      toast.success('Order updated');
      load();
    } catch (e) { toast.error('Reorder failed'); }
    setDragId(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setEditing('new')}
          className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs"
          data-testid="addons-cat-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Category
        </Button>
      </div>
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFDF8] text-left text-[11px] uppercase text-[#6B6B7A]">
            <tr>
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2 w-12">Icon</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2 text-center">Active</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-[#6B6B7A]"><RefreshCw className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-[#6B6B7A]">No categories yet.</td></tr>
            ) : rows.map((c) => (
              <tr key={c.category_id}
                draggable onDragStart={() => setDragId(c.category_id)}
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDropReorder(c)}
                className={`border-t border-[#F0EDE6] hover:bg-[#FFFDF8] ${dragId === c.category_id ? 'opacity-50' : ''}`}
                data-testid={`addons-cat-row-${c.category_id}`}>
                <td className="px-3 py-2 text-[#6B6B7A]"><GripVertical className="w-4 h-4 cursor-grab" /></td>
                <td className="px-3 py-2 text-2xl">{c.icon || '—'}</td>
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2 text-center">
                  <Switch checked={!!c.active} onCheckedChange={() => onToggle(c)} />
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(c)} className="h-7 w-7 p-0"><Edit2 className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(c)} className="h-7 w-7 p-0 text-[#D4302A]"><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <CategoryEditor cat={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
};

const CategoryEditor = ({ cat, onClose, onSaved }) => {
  const [name, setName] = useState(cat?.name || '');
  const [icon, setIcon] = useState(cat?.icon || '');
  const [active, setActive] = useState(cat?.active ?? true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), icon: icon.trim() || null, active };
      if (cat) {
        await axios.put(`${API}/api/admin/addons/categories/${cat.category_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/admin/addons/categories`, payload, { withCredentials: true });
      }
      toast.success('Saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-sm">
        <DialogHeader>
          <DialogTitle>{cat ? 'Edit Category' : 'New Category'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="addons-cat-name" />
          </div>
          <div>
            <label className="block text-[11px] uppercase text-[#6B6B7A] mb-1">Emoji / Icon</label>
            <Input value={icon} onChange={(e) => setIcon(e.target.value)} maxLength={4} placeholder="🎈" className="h-9 rounded-xl border-[#E8E5DF] text-sm w-24" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={active} onCheckedChange={setActive} />
            <span className="text-sm">Active</span>
          </div>
        </div>
        <DialogFooter className="gap-2 pt-3">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="addons-cat-save">
            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CrmAddons;
